import React, { useEffect, useRef } from 'react';
import BpmnViewer from 'bpmn-js/lib/NavigatedViewer';
import { leverandorApi } from '../services/rtkApi';
import { useParams } from 'react-router-dom';
import { Loader } from '@klp/design/Loader';
import { StackdriverLogger } from '@klpkonsern/stackdriver-logger';
import { isFetchBaseQueryError, isSerializedError } from '@forvalter/utils';
import { Button, Grid, makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Main } from '@klp/layout';
import { LeverandorRoutes } from '../config';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { ActionLink, Block, Typography } from '@klp/design';

const useStyles = makeStyles((theme) => ({
    readOnlyField: {
        backgroundColor: theme.palette.primary.veryLight,
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'transparent', // Hides the border
        },
        '& .MuiOutlinedInput-input': {
            cursor: 'not-allowed',
        },
        '& .MuiFormLabel-asterisk': {
            color: theme.palette.error.main,
            fontSize: '1.5rem',
        },
    },
    editableField: {
        '& .MuiInputBase-root': {
            color: theme.palette.text.primary,
        },
        '& .Mui-disabled': {
            cursor: 'not-allowed',
        },
        '& .MuiFormLabel-asterisk': {
            color: theme.palette.error.main,
            fontSize: '1.5rem',
        },
    },
    bpmnViewerContainer: {
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        padding: '20px',
    },
    bpmnViewer: {
        flex: 1,
        width: '100%',
        height: '100%',
        position: 'relative',
        cursor: 'grab',
    },
    zoomControls: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        background: 'rgba(255, 255, 255, 0.8)',
        padding: '8px',
        borderRadius: '4px',
        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
    },
}));

export const LeverandorProsessDefinisjonDiagram = () => {
    const { t } = useTranslation();
    const classes = useStyles();
    const viewerRef = useRef<HTMLDivElement>(null);
    const bpmnViewerRef = useRef<BpmnViewer<HTMLDivElement>>();
    const { processDefinitionId } = useParams<{
        processDefinitionId: string;
    }>();

    const {
        data: processDefinitionXmlData,
        isLoading: processDefinitionXmlIsLoading,
        isFetching: processDefinitionXmlIsFetching,
        error: processDefinitionXmlError,
        isError: processDefinitionXmlIsError,
    } = leverandorApi.endpoints.getLeverandorProcessDefinitionXml.useQuery(
        {
            processDefinitionId,
        },
        { skip: !processDefinitionId },
    );

    const handleXMLImport = async (viewer: BpmnViewer<HTMLDivElement>) => {
        if (!processDefinitionXmlData) return;

        try {
            await viewer.importXML(processDefinitionXmlData);

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            viewer.get('canvas').zoom('fit-viewport');

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const viewbox = viewer.get('canvas').viewbox();

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            viewer.get('canvas').viewbox({
                x: viewbox.x,
                y: viewbox.y - 100,
                width: viewbox.width,
                height: viewbox.height,
            });
        } catch (error) {
            throw new Error('Error importing XML for diagram', error);
        }
    };

    useEffect(() => {
        const viewer = new BpmnViewer<HTMLDivElement>({
            container: viewerRef.current as HTMLDivElement,
        });
        bpmnViewerRef.current = viewer;

        handleXMLImport(viewer);

        return () => {
            viewer.destroy();
            bpmnViewerRef.current = undefined;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        processDefinitionXmlIsFetching,
        processDefinitionXmlIsLoading,
        processDefinitionXmlData,
    ]);

    const handleZoomIn = () => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const canvas = bpmnViewerRef.current?.get('canvas');

        if (canvas) {
            // @ts-expect-error Ignore canvas type
            canvas.zoom(canvas.zoom() + 0.2);
        }
    };

    const handleZoomOut = () => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const canvas = bpmnViewerRef.current?.get('canvas');

        if (canvas) {
            // @ts-expect-error Ignore canvas type
            canvas.zoom(canvas.zoom() - 0.2);
        }
    };

    const handleResetView = () => {
        const canvas = bpmnViewerRef.current?.get('canvas');

        let viewbox;
        if (canvas) {
            // @ts-expect-error Ignore canvas type
            viewbox = canvas.viewbox();
        }

        if (canvas) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            canvas.viewbox({
                x: viewbox.x,
                y: viewbox.y - 100,
                width: viewbox.width,
                height: viewbox.height,
            });
        }
    };

    if (processDefinitionXmlIsError) {
        if (isFetchBaseQueryError(processDefinitionXmlError)) {
            StackdriverLogger.log(
                new Error('Failed to fetch process definition XML', {
                    cause: {
                        errorType: 'FetchBaseQueryError',
                        status: processDefinitionXmlError.status,
                        data: processDefinitionXmlError.data,
                        processDefinitionId,
                    },
                }),
            );
        } else if (isSerializedError(processDefinitionXmlError)) {
            StackdriverLogger.log(
                new Error('Process definition XML serialization error', {
                    cause: {
                        errorType: 'SerializedError',
                        message: processDefinitionXmlError.message,
                        stack: processDefinitionXmlError.stack,
                        processDefinitionId,
                    },
                }),
            );
        } else {
            StackdriverLogger.log(
                new Error('Unknown process definition XML error', {
                    cause: {
                        errorType: 'Unknown',
                        error: processDefinitionXmlError,
                        processDefinitionId,
                    },
                }),
            );
        }
    }

    return (
        <Loader
            isLoading={
                processDefinitionXmlIsLoading || processDefinitionXmlIsFetching
            }
        >
            <Main>
                <Main.Breadcrumbs>
                    <Main.Crumb
                        id="leverandorSchema"
                        label="breadcrumbs.leverandor.schema"
                        href={LeverandorRoutes.leverandorSchema()}
                    />
                    <Main.Crumb
                        id="leverandorDokumentasjon"
                        label="leverandor.header.documentation"
                        href={LeverandorRoutes.leverandorSchemaDocumentationUrl()}
                    />
                    <Main.Crumb
                        id="leverandorDiagram"
                        label="leverandor.processDefinition.breadcrumb"
                        href={LeverandorRoutes.processdefinitionDiagram(
                            processDefinitionId,
                        )}
                    />
                </Main.Breadcrumbs>
                <Main.Navigation>
                    <ActionLink
                        actionType="linkBack"
                        translation="leverandor.processDefinition.back"
                        href={LeverandorRoutes.leverandorSchemaDocumentationUrl()}
                    />
                </Main.Navigation>
                <Grid container={true} direction="column">
                    <Typography
                        variant="h1"
                        translation="leverandor.processDefinition.title"
                        translationOptions={{
                            processDefinitionId: processDefinitionId ?? '',
                        }}
                    >
                        {processDefinitionId}
                    </Typography>
                    <br />

                    {processDefinitionXmlData && processDefinitionId ? (
                        <Block className={classes.bpmnViewerContainer}>
                            <div
                                ref={viewerRef}
                                className={classes.bpmnViewer}
                            />
                            <Block className={classes.zoomControls}>
                                <Button
                                    variant="outlined"
                                    title={t(
                                        'leverandor.processDefinition.zoomIn',
                                    )}
                                    onClick={handleZoomIn}
                                    endIcon={
                                        <Illustration
                                            icon={IconName.PLUS}
                                            klpPalette="primary"
                                            klpColor="main"
                                        />
                                    }
                                >
                                    {t('leverandor.processDefinition.zoomIn')}
                                </Button>
                                <Button
                                    variant="outlined"
                                    title={t(
                                        'leverandor.processDefinition.zoomOut',
                                    )}
                                    onClick={handleZoomOut}
                                    endIcon={
                                        <Illustration
                                            icon={IconName.MINUS}
                                            klpPalette="primary"
                                            klpColor="main"
                                        />
                                    }
                                >
                                    {t('leverandor.processDefinition.zoomOut')}
                                </Button>
                                <Button
                                    variant="outlined"
                                    title={t(
                                        'leverandor.processDefinition.resetZoom',
                                    )}
                                    onClick={handleResetView}
                                >
                                    {t(
                                        'leverandor.processDefinition.resetZoom',
                                    )}
                                </Button>
                            </Block>
                        </Block>
                    ) : null}
                </Grid>
            </Main>
        </Loader>
    );
};
